"use client";

// UIArc — source MIT (c) 2026 Elia Kuratli, arc-library@7238f981 registry/components/bar-chart/bar-chart.tsx.
// Import fidèle (ui/components) : imports canoniques Foundry, jetons --arc-* (outil U1 arc_tokens). Voir UI-BANK.md.
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type KeyboardEvent, type PointerEvent } from "react";
import { AnimatePresence, animate, cancelFrame, frame, motion, useInView, useMotionValue, usePresence, useReducedMotionConfig as useReducedMotion, useTransform, type MotionValue, type Variants } from "motion/react";
import { motionTokens } from "../../lib/motion-tokens";
import styles from "./bar-chart.module.css";

export interface BarChartDatum {
  /** Stable identity, such as an ISO date. A bar whose key survives a data change keeps its place, so switching ranges morphs instead of redrawing. */
  key: string;
  /** Full label for the headline and screen readers, such as "Tue, Sep 15". */
  label: string;
  /** Short label under the bar, such as "T" or "Sep 14". Leave it out to keep the axis quiet. */
  axisLabel?: string;
  value: number;
}

/** Use a bar chart for one measure across a run of days, weeks, or months, when people compare each period against the rest and against the average. */
export interface BarChartProps {
  data: BarChartDatum[];
  /** What is measured, such as "Active minutes". Names the chart for assistive technology. */
  label: string;
  /** The range on show, such as "Sep 15–21, 2026". It rests under the headline. */
  period: string;
  /** Unit after each value, such as "min". */
  unit?: string;
  /** Headline label at rest, above the average. */
  averageLabel?: string;
  /** Headline label while a bar is scrubbed. */
  valueLabel?: string;
  /** Header of the first column in the data table read by screen readers. */
  categoryLabel?: string;
  /** Draws the average as a reference line that springs to each new range. */
  showAverage?: boolean;
  /** Plot height in pixels. */
  height?: number;
  formatValue?: (value: number) => string;
  /** Foundry (Finance8) : présentation par défaut, colonnes dans le temps. */
  presentation?: "columns";
}

/**
 * Foundry, promotion additive (Finance8, 05/10/2026). A bullet bar: one actual against a target on a scale the caller shares
 * between rows, so every target tick lines up down a table. Qualitative bands sit behind it. The part past the target is the
 * SAME bar clipped to the space beyond the tick and painted in the danger color, so it appears exactly as the bar crosses the
 * tick with no second animation. A new value springs the bar from wherever it is; reduced motion jumps. The caller computes
 * shares, signs and sums; the chart owns geometry, paint and the spring.
 */
export interface BarChartBulletProps {
  presentation: "bullet";
  /** The actual, in the unit of `target` and `max` (a share of budget, say). Negative values draw nothing. */
  value: number;
  target: number;
  /** Right end of the shared scale. */
  max: number;
  /** Qualitative band edges, ascending, in the same unit, such as `[0.9, 1.1]`. Past the last edge the band steps darker. */
  bands?: readonly number[];
  /** Accessible name, such as "Engineering, 1.37M of 1.37M". */
  label: string;
  /** Hides the bar from assistive technology when its row already says the figures in words. */
  decorative?: boolean;
  size?: "sm" | "md";
  className?: string;
}

/** Foundry, promotion additive (Analytics15, 06/10/2026). One bar of a waterfall: a level standing from zero, or a signed movement. */
export interface BarChartWaterfallStep {
  /** Stable identity: a bar whose key survives a change springs to its new levels instead of being replaced. */
  key: string;
  /** Name of the bar, such as "Expansion". */
  label: string;
  /** Short name under the bar. Defaults to `label`. */
  axisLabel?: string;
  /** For a movement, the signed change; for a total, the level itself. */
  value: number;
  /** A total stands from zero (a start or an end level); a movement floats from the running level. */
  total?: boolean;
  /** Read with the bar after its amount, such as "14 accounts". */
  detail?: string;
}

/**
 * Foundry, promotion additive (Analytics15, 06/10/2026). A waterfall (bridge): totals stand from zero, each movement floats from
 * the running level the bars before it reached, rising when positive and falling when negative. The bars are one radio group:
 * arrows move the selection and wrap, Home and End jump. New steps spring every bar from its levels on screen; reduced motion
 * jumps. The caller supplies the steps; the chart sums nothing it was not given and never invents a total.
 */
export interface BarChartWaterfallProps {
  presentation: "waterfall";
  steps: BarChartWaterfallStep[];
  /** Names the chart and its radio group, such as "MRR bridge, September". */
  label: string;
  /** Controlled selected bar key, or null for none. */
  selected?: string | null;
  defaultSelected?: string | null;
  onSelectedChange?: (key: string) => void;
  /** Formats levels and the size of a movement (the chart writes the sign). */
  formatValue?: (value: number) => string;
  /**
   * `zero` (default) starts the axis at zero. `fit` starts it just under the lowest level, so small movements on a large base stay
   * readable; the totals are then cut at the bottom of the plot and the chart always says where the axis starts.
   */
  axis?: "zero" | "fit";
  height?: number;
  className?: string;
}

const { spring, duration, ease, blur, stagger } = motionTokens;
/** Motion drops inherited velocity on time-defined springs, so values that retarget mid-flight run the same springs written as stiffness and damping. */
const physical = ({ visualDuration, bounce }: { visualDuration: number; bounce: number }, restDelta = .01) => { const root = (2 * Math.PI) / (visualDuration * 1.2); return { type: "spring" as const, stiffness: root * root, damping: 2 * (1 - bounce) * root, restDelta, restSpeed: restDelta * 2 }; };
const settle = physical(spring.smooth);
const grow = physical(spring.morph);
const glide = physical(spring.snappy);
/** The zoom runs in fractions of the plot, so it rests far below a pixel. */
const zoom = physical(spring.smooth, .00001);
const collapse = physical({ visualDuration: duration.standard, bounce: 0 });
const fadeFast = { duration: duration.instant, ease: [...ease.standard] } as const;
/** Starts animations on the next frame, so the render that caused them never swallows a spring's first frames. Returns a cleanup. */
function soon(start: () => { stop: () => void }) {
  let controls: { stop: () => void } | null = null;
  const run = () => { controls = start(); };
  frame.update(run);
  return () => { cancelFrame(run); controls?.stop(); };
}

/** Room above the top gridline, the share of each slot a bar fills, its widest size, and the radius of its data end. */
const TOP = 12, FILL = .58, MAX_BAR = 28, RADIUS = 4;
const grouped = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/** Clean gridlines: the smallest step of 1, 2, 3, or 5 that covers the data in four rows or fewer, so shared steps survive a range change. */
function scaleFor(max: number) {
  const safe = Math.max(max, 1);
  const magnitude = 10 ** Math.floor(Math.log10(safe / 4));
  for (const factor of [1, 2, 3, 5, 10]) {
    const step = factor * magnitude, rows = Math.ceil(safe / step);
    if (rows <= 4) return { top: rows * step, ticks: Array.from({ length: rows + 1 }, (_, row) => row * step) };
  }
  return { top: safe, ticks: [0, safe] };
}

/** A column that grows from a square baseline to a softly rounded data end. */
function barPath(x: number, w: number, h: number, base: number) {
  if (w <= 0 || h <= .01) return `M${x.toFixed(2)} ${base}H${(x + Math.max(w, 0)).toFixed(2)}Z`;
  const r = Math.min(RADIUS, w / 2, h), top = base - h, f = (value: number) => value.toFixed(2);
  return `M${f(x)} ${base}V${f(top + r)}A${f(r)} ${f(r)} 0 0 1 ${f(x + r)} ${f(top)}H${f(x + w - r)}A${f(r)} ${f(r)} 0 0 1 ${f(x + w)} ${f(top + r)}V${base}Z`;
}

/** Slots count back from the latest bar, so the newest day stays pinned right while a longer range zooms out to the left. */
function usePlace(place: number, reduced: boolean) {
  const value = useMotionValue(place);
  useEffect(() => {
    if (value.get() === place) return;
    if (reduced) { value.jump(place); return; }
    return soon(() => animate(value, place, settle));
  }, [place, reduced, value]);
  return value;
}

type Frame = { width: MotionValue<number>; unit: MotionValue<number> };
const centerOf = ({ width, unit }: Frame, place: number) => width.get() - (place + .5) * width.get() * unit.get();

function Bar({ id, place, value, delay, shown, active, frame, scale, height, reduced }: { id: string; place: number; value: number; delay: number; shown: boolean; active: boolean; frame: Frame; scale: MotionValue<number>; height: number; reduced: boolean }) {
  const [isPresent, safeToRemove] = usePresence();
  const amount = useMotionValue(0);
  const slot = usePlace(place, reduced);
  const [firstDelay] = useState(delay);
  const grown = useRef(false);
  const remove = useRef(safeToRemove);
  useEffect(() => { remove.current = safeToRemove; }, [safeToRemove]);

  // A bar grows once it is seen, each a beat after its neighbour; later values settle without a delay.
  useEffect(() => {
    if (!isPresent) return;
    if (reduced) { amount.jump(value); grown.current = true; return; }
    if (!shown) return;
    const first = !grown.current;
    grown.current = true;
    return soon(() => animate(amount, value, first ? { ...grow, delay: firstDelay } : settle));
  }, [amount, firstDelay, isPresent, reduced, shown, value]);

  // A leaving bar sinks into the baseline, faster than a bar grows, while the zoom carries it out of the plot.
  useEffect(() => {
    if (isPresent) return;
    if (reduced) { remove.current?.(); return; }
    return soon(() => animate(amount, 0, { ...collapse, onComplete: () => remove.current?.() }));
  }, [amount, isPresent, reduced]);

  const d = useTransform(() => {
    const w = frame.width.get() * frame.unit.get(), size = Math.min(w * FILL, MAX_BAR), center = centerOf(frame, slot.get());
    return barPath(center - size / 2, size, (amount.get() / scale.get()) * (height - TOP), height);
  });
  return <motion.path className={styles.bar} data-key={id} data-active={active || undefined} d={d} />;
}

/** A value on the axis: its gridline and label ride the scale, so a taller range slides them down while new rows fade in above. */
function useRowY(value: number, scale: MotionValue<number>, height: number) {
  return useTransform(() => Math.round(height - (value / scale.get()) * (height - TOP)) + .5);
}

function Gridline({ value, scale, height }: { value: number; scale: MotionValue<number>; height: number }) {
  const y = useRowY(value, scale, height);
  if (value === 0) return null;
  return <motion.line className={styles.grid} x1={0} x2="100%" y1={y} y2={y} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: fadeFast }} transition={{ duration: duration.standard, ease: [...ease.standard] }} />;
}

function TickLabel({ value, scale, height, avoid, format }: { value: number; scale: MotionValue<number>; height: number; avoid: MotionValue<number> | null; format: (value: number) => string }) {
  const y = useRowY(value, scale, height);
  // A tick label makes way where the average label sits beside it.
  const opacity = useTransform(() => avoid ? clamp((Math.abs(y.get() - avoid.get()) - 10) / 6, 0, 1) : 1);
  return <motion.span className={styles.tick} style={{ y }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: fadeFast }} transition={{ duration: duration.standard, ease: [...ease.standard] }}>
    <motion.span className={styles.tickText} style={{ opacity }}>{format(value)}</motion.span>
  </motion.span>;
}

function AxisLabel({ text, size, shift, place, frame, reduced }: { text: string; size: number; shift: number; place: number; frame: Frame; reduced: boolean }) {
  const slot = usePlace(place, reduced);
  // Centred under its bar and carried by the zoom. A label at either end keeps the nudge that holds it inside the settled plot.
  const x = useTransform(() => centerOf(frame, slot.get()) - size / 2 + shift);
  return <motion.span className={styles.axisLabel} style={{ x }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: fadeFast }} transition={{ duration: duration.standard, ease: [...ease.standard] }}>{text}</motion.span>;
}

/** Axis labels that fit once the range has settled: every label, else every second, third, and so on, counted back from the latest so the newest date always shows and spacing stays even. */
function fittingLabels(data: BarChartDatum[], width: number, sizes: Record<string, number>) {
  const fitting = new Map<string, number>();
  if (!width) return fitting;
  const count = Math.max(1, data.length);
  const labels = data.flatMap((item, index) => {
    const size = item.axisLabel ? sizes[item.axisLabel] : undefined;
    if (size === undefined) return [];
    const natural = width - (data.length - 1 - index + .5) * (width / count) - size / 2;
    return [{ key: item.key, size, natural, left: clamp(natural, 0, Math.max(0, width - size)) }];
  }).reverse();
  for (let stride = 1; stride <= labels.length; stride++) {
    const picked = labels.filter((_, rank) => rank % stride === 0);
    if (stride === labels.length || picked.every((label, rank) => { const before = picked[rank - 1]; return before === undefined || label.left + label.size + 10 <= before.left; })) {
      picked.forEach(label => fitting.set(label.key, label.left - label.natural));
      break;
    }
  }
  return fitting;
}

/** Changed text rises from a soft blur in the direction it moved, and leaves a little faster than it arrives. */
const rise: Variants = {
  hidden: (direction: number) => ({ opacity: 0, y: `${.3 * direction}em`, filter: `blur(${blur.soft}px)` }),
  shown: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: duration.standard, ease: [...ease.enter] } },
  gone: (direction: number) => ({ opacity: 0, y: `${-.3 * direction}em`, filter: `blur(${blur.subtle}px)`, transition: { duration: duration.instant, ease: [...ease.standard] } }),
};
const fade: Variants = { hidden: { opacity: 0, y: 0, filter: "blur(0px)" }, shown: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: duration.fast } }, gone: { opacity: 0, y: 0, filter: "blur(0px)", transition: { duration: duration.instant } } };

function Swap({ text, direction, reduced }: { text: string; direction: number; reduced: boolean }) {
  return <span className={styles.swap}><AnimatePresence mode="popLayout" initial={false} custom={direction}>
    <motion.span key={text} className={styles.swapText} custom={direction} variants={reduced ? fade : rise} initial="hidden" animate="shown" exit="gone">{text}</motion.span>
  </AnimatePresence></span>;
}

/** A number rolls digit by digit, keyed by place from the right, so unchanged digits hold still. Its width springs to fit, so the unit beside it glides instead of jumping. */
function Roll({ text, direction, reduced }: { text: string; direction: number; reduced: boolean }) {
  const sizer = useRef<HTMLSpanElement>(null);
  const width = useMotionValue<number | "auto">("auto");
  useLayoutEffect(() => {
    const node = sizer.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    let measured: string | null = null;
    // Only a new value springs; a late web font or a resize jumps straight to fit.
    const observer = new ResizeObserver(() => {
      const next = node.offsetWidth;
      if (measured !== null && measured !== node.textContent && !reduced) animate(width, next, grow);
      else width.jump(next);
      measured = node.textContent;
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [reduced, width]);
  const chars = [...text];
  const variants = reduced ? fade : rise;
  return <motion.span className={styles.roll} style={{ width }}>
    <span ref={sizer} className={styles.sizer} aria-hidden="true">{text}</span>
    <span className={styles.digits}><AnimatePresence mode="popLayout" initial={false} custom={direction} anchorX="right">
      {chars.map((char, index) => <motion.span key={`p${chars.length - index}`} className={styles.digit} custom={direction} variants={variants} initial="hidden" animate="shown" exit="gone">
        <AnimatePresence mode="popLayout" initial={false} custom={direction} anchorX="right">
          <motion.span key={char} className={styles.digit} custom={direction} variants={variants} initial="hidden" animate="shown" exit="gone">{char}</motion.span>
        </AnimatePresence>
      </motion.span>)}
    </AnimatePresence></span>
  </motion.span>;
}

const subscribeNothing = () => () => {};
/** Reduced motion is only known in the browser, so the first client render matches the server before it takes effect. */
function useReducedMotionSafe() {
  const hydrated = useSyncExternalStore(subscribeNothing, () => true, () => false);
  return !!useReducedMotion() && hydrated;
}

/** Foundry (Finance8) — part [0, 1] d'une valeur sur l'échelle partagée ; une échelle nulle ou une valeur non finie ne dessine rien. */
const shareOf = (value: number, max: number) => max > 0 && Number.isFinite(value) ? clamp(value / max, 0, 1) : 0;

function BulletBar({ value, target, max, bands = [], label, decorative = false, size = "md", className }: BarChartBulletProps) {
  const reduced = useReducedMotionSafe();
  const share = shareOf(value, max), tick = shareOf(target, max);
  const fill = useMotionValue(share);
  // Un seul ressort critiquement amorti, interruptible : une nouvelle valeur repart d'où la barre en est.
  useEffect(() => {
    if (fill.get() === share) return;
    if (reduced) { fill.jump(share); return; }
    return soon(() => animate(fill, share, settle));
  }, [fill, reduced, share]);
  const edges = bands.map(edge => shareOf(edge, max)).sort((a, b) => a - b);
  const zones: { from: number; to: number; tone: "within" | "over" }[] = edges.map((edge, index) => { const next = edges[index + 1]; return next === undefined ? { from: edge, to: 1, tone: "over" } : { from: edge, to: next, tone: "within" }; });
  return <span className={[styles.bullet, className].filter(Boolean).join(" ")} data-size={size} role={decorative ? undefined : "img"} aria-label={decorative ? undefined : label} aria-hidden={decorative || undefined}>
    {zones.map(zone => <span key={`${zone.from}:${zone.tone}`} className={styles.bulletBand} data-tone={zone.tone} style={{ left: `${zone.from * 100}%`, width: `${Math.max(0, zone.to - zone.from) * 100}%` }} />)}
    <motion.span className={styles.bulletBar} style={{ scaleX: fill }} />
    <span className={styles.bulletOver} style={{ clipPath: `inset(0 0 0 ${(tick * 100).toFixed(3)}%)` }}><motion.span className={styles.bulletBar} data-over="" style={{ scaleX: fill }} /></span>
    <span className={styles.bulletTick} style={{ left: `${tick * 100}%` }} />
  </span>;
}

/** Foundry (Analytics15) — niveaux d'une cascade : un total part de zéro, un mouvement part du niveau courant. */
export function waterfallLevels(steps: readonly BarChartWaterfallStep[]) {
  let level = 0;
  return steps.map(step => {
    if (step.total) { level = step.value; return { key: step.key, from: 0, to: step.value }; }
    const from = level;
    level += step.value;
    return { key: step.key, from, to: level };
  });
}

/** Graduations propres sur [bas, haut], zéro inclus : pas de 1, 2, 2.5 ou 5 en quatre rangées au plus. */
function waterfallScale(low: number, high: number, fit = false) {
  // Ajusté : l'axe part sous le plus bas niveau (marge de 60 % de l'écart) sans descendre sous zéro ; sinon il part de zéro.
  const fitted = fit && low > 0, lo = fitted ? Math.max(0, low - (high - low) * .6) : Math.min(0, low);
  const hi = Math.max(fitted ? lo : 0, high, lo + 1), span = hi - lo, magnitude = 10 ** Math.floor(Math.log10(span / 4));
  for (const factor of [1, 2, 2.5, 5, 10]) {
    const step = factor * magnitude, bottom = Math.floor(lo / step) * step, top = Math.ceil(hi / step) * step;
    if ((top - bottom) / step <= 4 + 1e-9) return { min: bottom, max: top, ticks: Array.from({ length: Math.round((top - bottom) / step) + 1 }, (_, row) => Number((bottom + row * step).toPrecision(12))) };
  }
  return { min: lo, max: hi, ticks: [lo, hi] };
}

type Levels = { min: MotionValue<number>; max: MotionValue<number> };
/** Hauteur en % du tracé, bornée au tracé : une barre de total qui part sous un axe ajusté est coupée au bas, jamais dessinée dehors. */
const levelTop = (value: number, scale: Levels) => { const span = scale.max.get() - scale.min.get() || 1; return clamp((1 - (value - scale.min.get()) / span) * 100, 0, 100); };

/** Une barre de cascade : ses deux niveaux glissent sur le ressort ; montant au-dessus, trait vers la suivante au niveau atteint. */
function WaterfallBar({ step, from, to, at, count, scale, reduced, selected, preview, amount, connect }: { step: BarChartWaterfallStep; from: number; to: number; at: number; count: number; scale: Levels; reduced: boolean; selected: boolean; preview: boolean; amount: string; connect: boolean }) {
  const lo = useMotionValue(from), hi = useMotionValue(to);
  useEffect(() => {
    if (lo.get() === from && hi.get() === to) return;
    if (reduced) { lo.jump(from); hi.jump(to); return; }
    return soon(() => { const controls = [animate(lo, from, settle), animate(hi, to, settle)]; return { stop: () => controls.forEach(control => control.stop()) }; });
  }, [from, hi, lo, reduced, to]);
  const top = useTransform(() => `${levelTop(Math.max(lo.get(), hi.get()), scale).toFixed(3)}%`);
  const size = useTransform(() => `${Math.max(.4, levelTop(Math.min(lo.get(), hi.get()), scale) - levelTop(Math.max(lo.get(), hi.get()), scale)).toFixed(3)}%`);
  const reach = useTransform(() => `${levelTop(hi.get(), scale).toFixed(3)}%`);
  const tone = step.total ? "total" : step.value >= 0 ? "up" : "down";
  return <>
    <motion.span className={styles.wfBar} data-tone={tone} data-selected={selected || undefined} data-preview={preview || undefined} aria-hidden="true" style={{ top, height: size, left: `${((at + (1 - FILL) / 2) / count) * 100}%`, width: `${(FILL / count) * 100}%` }} />
    <motion.span className={styles.wfAmount} data-tone={tone} data-selected={selected || undefined} aria-hidden="true" style={{ top, left: `${((at + .5) / count) * 100}%` }}>{amount}</motion.span>
    {connect && <motion.span className={styles.wfConnector} aria-hidden="true" style={{ top: reach, left: `${((at + (1 + FILL) / 2) / count) * 100}%`, width: `${((1 - FILL) / count) * 100}%` }} />}
  </>;
}

function WaterfallGridline({ value, scale, format }: { value: number; scale: Levels; format: (value: number) => string }) {
  const top = useTransform(() => `${levelTop(value, scale).toFixed(3)}%`);
  return <motion.span className={styles.wfGrid} data-zero={value === 0 || undefined} style={{ top }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: fadeFast }} transition={{ duration: duration.standard, ease: [...ease.standard] }}>
    <span className={styles.wfTick}>{format(value)}</span>
  </motion.span>;
}

function WaterfallChart({ steps, label, selected, defaultSelected = null, onSelectedChange, formatValue = value => grouped.format(value), axis = "zero", height = 260, className }: BarChartWaterfallProps) {
  const reduced = useReducedMotionSafe();
  const [own, setOwn] = useState<string | null>(defaultSelected);
  const current = selected !== undefined ? selected : own;
  const [preview, setPreview] = useState<string | null>(null);
  const levels = useMemo(() => waterfallLevels(steps), [steps]);
  const all = levels.flatMap(level => [level.from, level.to]);
  // Niveaux atteints (bornes des barres hors totaux partis de zéro) : un axe ajusté se cale sur eux.
  const reached = axis === "fit" ? levels.flatMap((level, at) => steps[at]?.total ? [level.to] : [level.from, level.to]) : all;
  const range = waterfallScale(reached.length ? Math.min(...reached) : 0, reached.length ? Math.max(...reached) : 1, axis === "fit");
  const cut = range.min > 0;
  const scale = { min: useMotionValue(range.min), max: useMotionValue(range.max) };
  useEffect(() => {
    if (reduced) { scale.min.jump(range.min); scale.max.jump(range.max); return; }
    return soon(() => { const controls = [animate(scale.min, range.min, settle), animate(scale.max, range.max, settle)]; return { stop: () => controls.forEach(control => control.stop()) }; });
  }, [range.max, range.min, reduced, scale.max, scale.min]);
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const count = Math.max(1, steps.length);
  const selectedIndex = steps.findIndex(step => step.key === current);
  const choose = (key: string) => { if (key === current) return; if (selected === undefined) setOwn(key); onSelectedChange?.(key); };
  const signed = (value: number) => `${value > 0 ? "+" : value < 0 ? "−" : ""}${formatValue(Math.abs(value))}`;
  const describe = (step: BarChartWaterfallStep) => `${step.label}, ${step.total ? formatValue(step.value) : `${step.value >= 0 ? "up" : "down"} ${formatValue(Math.abs(step.value))}`}${step.detail ? `, ${step.detail}` : ""}`;
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, at: number) => {
    const last = steps.length - 1;
    const next = ({ ArrowRight: at === last ? 0 : at + 1, ArrowDown: at === last ? 0 : at + 1, ArrowLeft: at === 0 ? last : at - 1, ArrowUp: at === 0 ? last : at - 1, Home: 0, End: last } as Record<string, number>)[event.key];
    const step = next === undefined ? undefined : steps[next];
    if (!step) return;
    event.preventDefault();
    choose(step.key);
    buttons.current.get(step.key)?.focus();
  };
  return <figure className={[styles.waterfall, className].filter(Boolean).join(" ")} aria-label={label} data-active={(current !== null && selectedIndex >= 0) || preview ? true : undefined}>
    <div className={styles.wfChart}>
      <div className={styles.wfPlot} style={{ height }}>
        <AnimatePresence initial={false}>{range.ticks.map(value => <WaterfallGridline key={value} value={value} scale={scale} format={formatValue} />)}</AnimatePresence>
        {steps.map((step, at) => <WaterfallBar key={step.key} step={step} from={levels[at]!.from} to={levels[at]!.to} at={at} count={count} scale={scale} reduced={reduced} selected={step.key === current} preview={step.key === preview} amount={step.total ? formatValue(step.value) : signed(step.value)} connect={at < steps.length - 1} />)}
        <div className={styles.wfButtons} role="radiogroup" aria-label={label}>
          {steps.map((step, at) => <button key={step.key} ref={node => { if (node) buttons.current.set(step.key, node); else buttons.current.delete(step.key); }} type="button" role="radio" className={styles.wfButton} aria-checked={step.key === current} aria-label={describe(step)}
            tabIndex={step.key === current || (selectedIndex < 0 && at === 0) ? 0 : -1} style={{ left: `${(at / count) * 100}%`, width: `${(1 / count) * 100}%` }}
            onClick={() => choose(step.key)} onKeyDown={event => onKeyDown(event, at)}
            onPointerEnter={event => { if (event.pointerType === "mouse") setPreview(step.key); }} onPointerLeave={event => { if (event.pointerType === "mouse") setPreview(null); }} />)}
        </div>
      </div>
      <div className={styles.wfAxis} aria-hidden="true">{steps.map((step, at) => <span key={step.key} className={styles.wfAxisLabel} data-selected={step.key === current || undefined} style={{ left: `${(at / count) * 100}%`, width: `${(1 / count) * 100}%` }}>{step.axisLabel ?? step.label}</span>)}</div>
    </div>
    {cut ? <p className={styles.wfNote}>{`Axis starts at ${formatValue(range.min)}, not zero`}</p> : null}
    <p className={styles.srOnly}>{`${label}. ${steps.map(describe).join(". ")}.`}</p>
  </figure>;
}

/** Foundry (Finance8) : une seule entrée canon — colonnes dans le temps (défaut, inchangé), barre bullet ; (Analytics15) cascade. */
export function BarChart(props: BarChartProps | BarChartBulletProps | BarChartWaterfallProps) {
  const invalid = props.presentation === "bullet"
    ? [props.value, props.target, props.max, ...(props.bands ?? [])].some(value => !Number.isFinite(value))
    : props.presentation === "waterfall"
      ? props.steps.some(step => !Number.isFinite(step.value))
      : props.data.some(datum => !Number.isFinite(datum.value));
  if (invalid) return <span role="status">{props.label} : Donnée indisponible</span>;
  if (props.presentation === "bullet") return <BulletBar {...props} />;
  if (props.presentation === "waterfall") return <WaterfallChart {...props} />;
  return <ColumnsChart {...props} />;
}

function ColumnsChart({ data, label, period, unit = "", averageLabel = "Daily average", valueLabel = "Total", categoryLabel = "Day", showAverage = true, height = 176, formatValue = value => grouped.format(value) }: BarChartProps) {
  const reduced = useReducedMotionSafe();
  const figure = useRef<HTMLElement>(null);
  const plot = useRef<HTMLDivElement>(null);
  const inView = useInView(figure, { once: true, amount: .35 });
  const titleId = useId();
  const count = Math.max(data.length, 1), last = data.length - 1;
  const average = data.length ? data.reduce((sum, item) => sum + item.value, 0) / data.length : 0;
  const peak = data.reduce((max, item) => Math.max(max, item.value), 0);
  const { top, ticks } = useMemo(() => scaleFor(peak), [peak]);
  const signature = data.map(item => item.key).join("|");
  const suffix = unit ? ` ${unit}` : "";

  // Bars that join an existing chart grow outward from the ones that stayed; the first reveal runs left to right.
  const [known, setKnown] = useState(() => ({ signature, keys: new Set(data.map(item => item.key)), fresh: new Map<string, number>(), changes: 0 }));
  const [active, setActive] = useState<number | null>(null);
  if (known.signature !== signature) {
    const incoming = data.map((item, index) => ({ key: item.key, place: last - index })).filter(item => !known.keys.has(item.key)).sort((a, b) => a.place - b.place);
    const step = Math.min(stagger.item, .32 / Math.max(1, incoming.length));
    setKnown({ signature, keys: new Set(data.map(item => item.key)), fresh: new Map(incoming.map((item, rank) => [item.key, rank * step])), changes: known.changes + 1 });
    setActive(null);
  }
  const revealStep = Math.min(stagger.item, .36 / count);
  const index = active === null ? null : Math.min(active, last);
  // La barre parcourue : `index` est borné à `last`, donc présente dès qu'il y a des données (identique à l'ancien test).
  const scrubbedItem = index === null ? undefined : data[index];
  const scrubbing = scrubbedItem !== undefined;
  const shownValue = scrubbedItem ? scrubbedItem.value : average;
  // Headline copy moves the way the data did: a larger value rises from below, a later day arrives from below too.
  const [trend, setTrend] = useState({ value: shownValue, index, valueWay: 1, indexWay: 1 });
  if (trend.value !== shownValue || trend.index !== index) setTrend({ value: shownValue, index, valueWay: shownValue === trend.value ? trend.valueWay : shownValue > trend.value ? 1 : -1, indexWay: index === null || trend.index === null ? 1 : Math.sign(index - trend.index) || 1 });

  const width = useMotionValue(0);
  const unitShare = useMotionValue(1 / count);
  const scale = useMotionValue(top);
  const mean = useMotionValue(0);
  const cursor = useMotionValue(0);
  const frame = useMemo(() => ({ width, unit: unitShare }), [width, unitShare]);

  // The chart draws in its own pixels; the width is read before paint and followed on resize. Axis labels wait for it, then thin out where they would collide.
  const [plotWidth, setPlotWidth] = useState(0);
  const [labelSizes, setLabelSizes] = useState<Record<string, number>>({});
  const measure = useRef<HTMLSpanElement>(null);
  const labelTexts = [...new Set(data.map(item => item.axisLabel).filter((text): text is string => !!text))].join("\n");
  useLayoutEffect(() => {
    const node = plot.current, ruler = measure.current;
    if (!node || !ruler) return;
    const read = () => {
      width.set(node.clientWidth);
      setPlotWidth(node.clientWidth);
      const sizes: Record<string, number> = {};
      ruler.querySelectorAll<HTMLElement>("[data-text]").forEach(item => { sizes[item.dataset.text ?? ""] = item.offsetWidth; });
      setLabelSizes(current => Object.keys(sizes).every(text => current[text] === sizes[text]) ? current : sizes);
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(node);
    observer.observe(ruler);
    return () => observer.disconnect();
  }, [labelTexts, width]);
  const shownLabels = useMemo(() => fittingLabels(data, plotWidth, labelSizes), [data, plotWidth, labelSizes]);

  // A new range zooms the time axis around the latest bar, rescales the gridlines, and moves the average, all on springs that can be retargeted mid-flight.
  useEffect(() => {
    if (reduced) { unitShare.jump(1 / count); scale.jump(top); return; }
    return soon(() => { const controls = [animate(unitShare, 1 / count, zoom), animate(scale, top, settle)]; return { stop: () => controls.forEach(control => control.stop()) }; });
  }, [count, reduced, scale, top, unitShare]);
  // The average rises with the first bars, then springs to each new range.
  const meanStarted = useRef(false);
  useEffect(() => {
    if (reduced) { mean.jump(average); return; }
    if (!inView) return;
    const first = !meanStarted.current;
    meanStarted.current = true;
    return soon(() => animate(mean, average, first ? { ...settle, delay: .12 } : settle));
  }, [average, inView, mean, reduced]);

  // The cursor appears on the first scrubbed bar and glides between the ones after it.
  const wasScrubbing = useRef(false);
  useEffect(() => {
    if (index === null) { wasScrubbing.current = false; return; }
    const place = last - index;
    if (reduced || !wasScrubbing.current) cursor.jump(place);
    wasScrubbing.current = true;
    if (reduced) return;
    const controls = animate(cursor, place, glide);
    return () => controls.stop();
  }, [cursor, index, last, reduced]);

  const cursorX = useTransform(() => Math.round(centerOf(frame, cursor.get())) + .5);
  const meanY = useTransform(() => Math.round(height - (mean.get() / scale.get()) * (height - TOP)) + .5);
  const meanText = useTransform(() => `Moy. ${formatValue(Math.round(mean.get()))}`);

  const pointAt = (clientX: number) => {
    const rect = plot.current?.getBoundingClientRect();
    if (!rect?.width) return last;
    const place = Math.floor(((rect.right - clientX) / rect.width) * count);
    return clamp(last - place, 0, last);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && scrubbing) { event.preventDefault(); setActive(null); return; }
    const from = index ?? last + 1, page = Math.min(7, count);
    const next = ({ ArrowLeft: from - 1, ArrowDown: from - 1, ArrowRight: index === null ? last : from + 1, ArrowUp: index === null ? last : from + 1, PageDown: from - page, PageUp: from + page, Home: 0, End: last } as Record<string, number>)[event.key];
    if (next === undefined || last < 0) return;
    event.preventDefault();
    setActive(clamp(next, 0, last));
  };
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") event.currentTarget.setPointerCapture?.(event.pointerId);
    setActive(pointAt(event.clientX));
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => { if (event.pointerType === "mouse" || event.buttons) setActive(pointAt(event.clientX)); };
  const release = (event: PointerEvent<HTMLDivElement>) => { if (event.pointerType !== "mouse") setActive(null); };

  const scrubbed = scrubbedItem ?? null;
  const reading = scrubbed ?? data[last];
  const valueText = reading ? `${reading.label}: ${formatValue(reading.value)}${suffix}` : "Aucune donnée";
  const highest = data.reduce<BarChartDatum | null>((best, item) => !best || item.value > best.value ? item : best, null);
  const lowest = data.reduce<BarChartDatum | null>((best, item) => !best || item.value < best.value ? item : best, null);
  const summary = `${label}, ${period}. ${averageLabel} ${formatValue(Math.round(average))}${suffix}.${highest ? ` Maximum : ${highest.label}, ${formatValue(highest.value)}${suffix}.` : ""}${lowest && lowest !== highest ? ` Minimum : ${lowest.label}, ${formatValue(lowest.value)}${suffix}.` : ""}`;
  const shown = inView || reduced;
  const avoid = showAverage ? meanY : null;

  return <figure ref={figure} className={styles.figure} aria-labelledby={titleId}>
    <figcaption id={titleId} className={styles.srOnly}>{label}, {period}</figcaption>
    <div className={styles.header} aria-hidden="true">
      <span className={styles.kind}><Swap text={scrubbing ? valueLabel : averageLabel} direction={1} reduced={reduced} /></span>
      <span className={styles.value}><Roll text={formatValue(scrubbedItem ? scrubbedItem.value : Math.round(average))} direction={trend.valueWay} reduced={reduced} />{unit && <span className={styles.unit}>{unit}</span>}</span>
      <span className={styles.when}><Swap text={scrubbed ? scrubbed.label : period} direction={scrubbing ? trend.indexWay : 1} reduced={reduced} /></span>
    </div>
    <div className={styles.chart} data-scrubbing={scrubbing || undefined}>
      <div ref={plot} className={styles.plot} style={{ height }}>
        <svg className={styles.svg} width="100%" height={height} role="img" aria-label={summary}>
          <AnimatePresence initial={false}>{ticks.map(value => <Gridline key={value} value={value} scale={scale} height={height} />)}</AnimatePresence>
          <motion.line className={styles.cursor} x1={cursorX} x2={cursorX} y1={TOP} y2={height} initial={false} animate={{ opacity: scrubbing ? 1 : 0 }} transition={reduced ? { duration: 0 } : fadeFast} />
          <AnimatePresence initial={false}>
            {data.map((item, at) => <Bar key={item.key} id={item.key} place={last - at} value={item.value} delay={known.fresh.get(item.key) ?? at * revealStep} shown={shown} active={index === at} frame={frame} scale={scale} height={height} reduced={reduced} />)}
          </AnimatePresence>
          <line className={styles.baseline} x1={0} x2="100%" y1={height - .5} y2={height - .5} />
          {showAverage && <motion.line className={styles.mean} x1={0} x2="100%" y1={meanY} y2={meanY} initial={false} animate={{ opacity: shown ? 1 : 0 }} transition={reduced ? { duration: 0 } : { duration: duration.standard, delay: .12 }} />}
        </svg>
      </div>
      <div className={styles.gutter} aria-hidden="true">
        <AnimatePresence initial={false}>{ticks.map(value => <TickLabel key={value} value={value} scale={scale} height={height} avoid={avoid} format={formatValue} />)}</AnimatePresence>
        {showAverage && <motion.span className={styles.meanLabel} style={{ y: meanY }} initial={false} animate={{ opacity: shown ? 1 : 0 }} transition={reduced ? { duration: 0 } : { duration: duration.standard, delay: .12 }}><motion.span className={styles.tickText}>{meanText}</motion.span></motion.span>}
      </div>
      <div className={styles.axis} aria-hidden="true">
        <AnimatePresence initial={false}>
          {data.map((item, at) => item.axisLabel && shownLabels.has(item.key) ? <AxisLabel key={`${item.key}:${item.axisLabel}`} text={item.axisLabel} size={labelSizes[item.axisLabel] ?? 0} shift={shownLabels.get(item.key) ?? 0} place={last - at} frame={frame} reduced={reduced} /> : null)}
        </AnimatePresence>
        <span ref={measure} className={styles.measure}>{labelTexts.split("\n").filter(Boolean).map(text => <span key={text} data-text={text}>{text}</span>)}</span>
      </div>
      {/* The scrubber lies over the plot: hover or drag across it, or focus it and use the arrow keys. Vertical swipes still scroll the page. */}
      <div className={styles.scrubber} role={data.length ? "slider" : "group"} tabIndex={data.length ? 0 : -1} aria-label={`${label}, explorer par ${categoryLabel.toLowerCase()}`} aria-orientation="horizontal" aria-valuemin={data.length ? 1 : undefined} aria-valuemax={data.length || undefined} aria-valuenow={data.length ? (index ?? last) + 1 : undefined} aria-valuetext={valueText}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={release} onPointerCancel={() => setActive(null)} onPointerLeave={event => { if (event.pointerType === "mouse") setActive(null); }} onKeyDown={onKeyDown} onBlur={() => setActive(null)}
        onFocus={event => { if (event.currentTarget.matches(":focus-visible") && last >= 0) setActive(current => current ?? last); }} />
    </div>
    <table className={styles.srOnly}>
      <caption>{label}, {period}</caption>
      <thead><tr><th scope="col">{categoryLabel}</th><th scope="col">{label}</th></tr></thead>
      <tbody>{data.map(item => <tr key={item.key}><th scope="row">{item.label}</th><td>{formatValue(item.value)}{suffix}</td></tr>)}</tbody>
    </table>
    <p className={styles.srOnly} aria-live="polite" aria-atomic="true">{known.changes ? `${period}. ${averageLabel} ${formatValue(Math.round(average))}${suffix}.` : ""}</p>
  </figure>;
}

export default BarChart;
